import compromissoCompraVenda from "./compromisso-compra-venda";
import compromissoCompraVendaFormSchema from "./compromisso-compra-venda.form";
import contratoLocacaoImovel from "./contrato-locacao-imovel";
import contratoLocacaoImovelFormSchema from "./contrato-locacao-imovel.form";
import autorizacaoLocacaoImovel from "./autorizacao-locacao-imovel";
import autorizacaoLocacaoImovelFormSchema from "./autorizacao-locacao-imovel.form";
import propostaCompraImovel from "./proposta-compra-imovel";
import propostaCompraImovelFormSchema from "./proposta-compra-imovel.form";
import contratoParceriaVendas from "./contrato-parceria-vendas";
import contratoParceriaVendasFormSchema from "./contrato-parceria-vendas.form";
import reciboSimples from "./recibo-simples";
import reciboSimplesFormSchema from "./recibo-simples.form";
import autorizacaoGlebaLoteamento from "./autorizacao-gleba-loteamento";
import autorizacaoGlebaLoteamentoFormSchema from "./autorizacao-gleba-loteamento.form";
import autorizacaoVendaImovel from "./autorizacao-venda-imovel";
import autorizacaoVendaImovelFormSchema from "./autorizacao-venda-imovel.form";

/**
 * Single source of truth for each document type.
 * Adding a new document only requires adding a new definition here.
 */
export const documentDefinitions = {
  "compromisso-compra-venda": {
    id: "compromisso-compra-venda",
    label: "Compromisso de Compra e Venda",
    template: compromissoCompraVenda,
    formSchema: compromissoCompraVendaFormSchema,
    /**
     * Wizard steps for the generator (document header is step 0 in the page).
     * Each step lists `sectionId` values from the form schema.
     * `review: true` shows the full form (all sections) for a final check.
     */
    formSteps: [
      { id: "documento", label: "Documento" },
      {
        id: "partes",
        label: "Partes",
        sectionIds: ["vendedores", "compradores"],
      },
      { id: "testemunhas", label: "Testemunhas", sectionIds: ["testemunhas"] },
      { id: "imovel", label: "Imóvel", sectionIds: ["tipo-imovel"] },
      {
        id: "comissao-imobiliaria",
        label: "Comissão e imobiliária",
        sectionIds: ["tipo-comissao"],
      },
      {
        id: "pagamento",
        label: "Pagamento",
        sectionIds: ["tipo-pagamento"],
      },
      {
        id: "finalizacao",
        label: "Dados do contrato",
        sectionIds: ["dados-contrato"],
      },
      { id: "revisar", label: "Revisar", review: true },
    ],
    /** Repeatable groupKeys + labels for “Adicionar cliente” in the generator */
    customerImport: {
      enabled: true,
      targets: [
        { groupKey: "vendedores", label: "Vendedor" },
        { groupKey: "compradores", label: "Comprador" },
      ],
    },
    preview: {
      scrollMapping: {
        vendedores: "VENDEDORES",
        compradores: "COMPRADORES",
        testemunhas: "ASSINATURA_TESTEMUNHA",
        tipo_imovel: "TIPO_IMOVEL",
        tipo_pagamento: "TIPO_PAGAMENTO",
        tipo_comissao: "TIPO_COMISSAO",
      },
    },
  },

  "contrato-locacao-imovel": {
    id: "contrato-locacao-imovel",
    label: "Contrato de Locacao de Imovel",
    template: contratoLocacaoImovel,
    formSchema: contratoLocacaoImovelFormSchema,
    formSteps: [
      { id: "documento", label: "Documento" },
      {
        id: "partes",
        label: "Partes",
        sectionIds: ["locadores", "locatarios", "testemunhas"],
      },
      { id: "imovel", label: "Imovel", sectionIds: ["tipo-imovel"] },
      {
        id: "locacao-garantia",
        label: "Locacao e garantia",
        sectionIds: [
          "uso-locacao",
          "clausula-garantia",
          "fiadores",
          "dados-financeiros",
        ],
      },
      {
        id: "dados-gerais",
        label: "Dados gerais",
        sectionIds: ["dados-gerais"],
      },
      { id: "revisar", label: "Revisar", review: true },
    ],
    customerImport: {
      enabled: true,
      targets: [
        { groupKey: "locadores", label: "Locador" },
        { groupKey: "locatarios", label: "Locatario" },
        { groupKey: "fiadores", label: "Fiador" },
      ],
    },
    preview: {
      scrollMapping: {
        locadores: "LOCADORES",
        locatarios: "LOCATARIOS",
        fiadores: "FIADORES",
        testemunhas: "ASSINATURA_TESTEMUNHA",
        tipo_imovel: "TIPO_IMOVEL",
        USO_LOCACAO: "USO_LOCACAO",
        clausula_garantia: "CLAUSULA_GARANTIA",
      },
    },
  },

  "autorizacao-locacao-imovel": {
    id: "autorizacao-locacao-imovel",
    label: "Autorização para Locação de Imóvel",
    template: autorizacaoLocacaoImovel,
    formSchema: autorizacaoLocacaoImovelFormSchema,
    formSteps: [
      { id: "documento", label: "Documento" },
      {
        id: "partes",
        label: "Partes",
        sectionIds: ["proprietarios", "intermediador", "testemunhas"],
      },
      {
        id: "imovel",
        label: "Imóvel",
        sectionIds: ["tipo-imovel"],
      },
      {
        id: "termos",
        label: "Modalidade e dados",
        sectionIds: ["dados-autorizacao"],
      },
      {
        id: "assinatura",
        label: "Assinatura",
        sectionIds: ["dados-contrato"],
      },
      { id: "revisar", label: "Revisar", review: true },
    ],
    customerImport: {
      enabled: true,
      targets: [{ groupKey: "proprietarios", label: "Proprietário" }],
    },
    preview: {
      scrollMapping: {
        proprietarios: "PROPRIETARIO",
        intermediador: "INTERMEDIADOR",
        testemunhas: "ASSINATURA_TESTEMUNHAS",
        tipo_imovel: "TIPO_IMOVEL",
        EXCLUSIVIDADE: "EXCLUSIVIDADE",
        PRAZO_AUTORIZACAO: "PRAZO_AUTORIZACAO",
        PERCENTUAL_COMISSAO_CAPTACAO: "PERCENTUAL_COMISSAO_CAPTACAO",
        PERCENTUAL_ADMINISTRACAO: "PERCENTUAL_ADMINISTRACAO",
      },
    },
  },

  "proposta-compra-imovel": {
    id: "proposta-compra-imovel",
    label: "Proposta de Compra de Imóvel",
    template: propostaCompraImovel,
    formSchema: propostaCompraImovelFormSchema,
    formSteps: [
      { id: "documento", label: "Documento" },
      {
        id: "partes",
        label: "Partes",
        sectionIds: ["vendedores", "compradores", "testemunhas"],
      },
      { id: "imovel", label: "Imóvel", sectionIds: ["tipo-imovel"] },
      {
        id: "valores",
        label: "Valores e prazos",
        sectionIds: ["dados-proposta"],
      },
      {
        id: "assinatura",
        label: "Assinatura",
        sectionIds: ["dados-contrato"],
      },
      { id: "revisar", label: "Revisar", review: true },
    ],
    customerImport: {
      enabled: true,
      targets: [
        { groupKey: "vendedores", label: "Vendedor" },
        { groupKey: "compradores", label: "Comprador" },
      ],
    },
    preview: {
      scrollMapping: {
        vendedores: "VENDEDORES",
        compradores: "COMPRADORES",
        testemunhas: "ASSINATURA_TESTEMUNHAS",
        tipo_imovel: "TIPO_IMOVEL",
        tipo_pagamento: "TIPO_PAGAMENTO",
        VALOR_TOTAL: "VALOR_TOTAL",
        VALIDADE_PROPOSTA: "VALIDADE_PROPOSTA",
        FORO: "FORO",
      },
    },
  },

  "contrato-parceria-vendas": {
    id: "contrato-parceria-vendas",
    label: "Contrato de Parceria para Vendas",
    template: contratoParceriaVendas,
    formSchema: contratoParceriaVendasFormSchema,
    formSteps: [
      { id: "documento", label: "Documento" },
      {
        id: "partes",
        label: "Partes",
        sectionIds: ["captantes", "intermediadores"],
      },
      {
        id: "testemunhas",
        label: "Testemunhas",
        sectionIds: ["testemunhas"],
      },
      {
        id: "dados-contrato",
        label: "Dados do contrato",
        sectionIds: ["dados-contrato"],
      },
      { id: "revisar", label: "Revisar", review: true },
    ],
    customerImport: {
      enabled: true,
      targets: [
        { groupKey: "captantes", label: "Captante" },
        { groupKey: "intermediadores", label: "Intermediador" },
      ],
    },
    preview: {
      scrollMapping: {
        captantes: "CAPTANTES",
        intermediadores: "INTERMEDIADORES",
        DESCRICAO_PRODUTO: "DESCRICAO_PRODUTO",
        COMISSAO_INTERMEDIADOR: "COMISSAO_INTERMEDIADOR",
        MULTA_CONTRATUAL: "MULTA_CONTRATUAL",
        FORO: "FORO",
        testemunhas: "ASSINATURA_TESTEMUNHAS",
        ASSINATURA_TESTEMUNHAS: "ASSINATURA_TESTEMUNHAS",
      },
    },
  },

  "autorizacao-gleba-loteamento": {
    id: "autorizacao-gleba-loteamento",
    label: "Autorização Gleba Loteamento",
    template: autorizacaoGlebaLoteamento,
    formSchema: autorizacaoGlebaLoteamentoFormSchema,
    formSteps: [
      { id: "documento", label: "Documento" },
      {
        id: "partes",
        label: "Partes",
        sectionIds: ["proprietarios", "intermediador", "testemunhas"],
      },
      {
        id: "imovel",
        label: "Imóvel",
        sectionIds: ["dados-imovel"],
      },
      {
        id: "termos",
        label: "Modalidade e valores",
        sectionIds: ["dados-autorizacao"],
      },
      {
        id: "assinatura",
        label: "Assinatura",
        sectionIds: ["dados-contrato"],
      },
      { id: "revisar", label: "Revisar", review: true },
    ],
    customerImport: {
      enabled: true,
      targets: [{ groupKey: "proprietarios", label: "Proprietário" }],
    },
    preview: {
      scrollMapping: {
        proprietarios: "PROPRIETARIOS",
        intermediador: "INTERMEDIADOR",
        testemunhas: "ASSINATURA_TESTEMUNHAS",
        MATRICULA_IMOVEL: "MATRICULA_IMOVEL",
        EXCLUSIVIDADE: "EXCLUSIVIDADE",
        PRAZO_AUTORIZACAO: "PRAZO_AUTORIZACAO",
        PERCENTUAL_COMISSAO: "PERCENTUAL_COMISSAO",
        VALOR_IMOVEL: "VALOR_IMOVEL",
        COMARCA_FORO: "COMARCA_FORO",
      },
    },
  },

  "autorizacao-venda-imovel": {
    id: "autorizacao-venda-imovel",
    label: "Autorização para Venda de Imóvel",
    template: autorizacaoVendaImovel,
    formSchema: autorizacaoVendaImovelFormSchema,
    formSteps: [
      { id: "documento", label: "Documento" },
      {
        id: "partes",
        label: "Partes",
        sectionIds: ["proprietarios", "intermediador", "testemunhas"],
      },
      {
        id: "imovel",
        label: "Imóvel",
        sectionIds: ["tipo-imovel"],
      },
      {
        id: "termos",
        label: "Modalidade e comissão",
        sectionIds: ["dados-autorizacao"],
      },
      {
        id: "assinatura",
        label: "Assinatura",
        sectionIds: ["dados-contrato"],
      },
      { id: "revisar", label: "Revisar", review: true },
    ],
    customerImport: {
      enabled: true,
      targets: [{ groupKey: "proprietarios", label: "Proprietário" }],
    },
    preview: {
      scrollMapping: {
        proprietarios: "PROPRIETARIOS",
        intermediador: "INTERMEDIADOR",
        testemunhas: "ASSINATURA_TESTEMUNHAS",
        tipo_imovel: "TIPO_IMOVEL",
        EXCLUSIVIDADE: "EXCLUSIVIDADE",
        PRAZO_AUTORIZACAO: "PRAZO_AUTORIZACAO",
        COMISSAO: "COMISSAO",
      },
    },
  },

  "recibo-simples": {
    id: "recibo-simples",
    label: "Recibo Simples",
    template: reciboSimples,
    formSchema: reciboSimplesFormSchema,
    formSteps: [
      { id: "documento", label: "Documento" },
      {
        id: "dados-recibo",
        label: "Dados do recibo",
        sectionIds: ["dados-pagador", "dados-recibo"],
      },
      {
        id: "recebedor",
        label: "Recebedor",
        sectionIds: ["recebedor"],
      },
      {
        id: "finalizacao",
        label: "Local e data",
        sectionIds: ["dados-assinatura"],
      },
      { id: "revisar", label: "Revisar", review: true },
    ],
    preview: {
      scrollMapping: {
        NOME_PESSOA_FISICA: "NOME_PESSOA_FISICA",
        CPF_CNPJ_PAGADOR: "NOME_PESSOA_FISICA",
        VALOR: "VALOR",
        REFERENTE: "REFERENTE",
        FORMA_PAGAMENTO: "FORMA_PAGAMENTO",
        NOME_RECEBEDOR: "ASSINATURA_RECEBEDOR",
        CPF_CNPJ_RECEBEDOR: "ASSINATURA_RECEBEDOR",
        CIDADE: "CIDADE",
        DATA: "DATA",
      },
    },
  },
};

/** @deprecated Use documentDefinitions[id] instead. */
export const documentsRegistry = Object.fromEntries(
  Object.entries(documentDefinitions).map(([id, def]) => [id, def.template])
);

/** @deprecated Use documentDefinitions[id].formSchema instead. */
export const formRegistry = Object.fromEntries(
  Object.entries(documentDefinitions).map(([id, def]) => [id, def.formSchema])
);

/** Returns the template for a document (backwards compatible). */
export const getDocumentById = (id) => documentDefinitions[id]?.template;
